import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Promise<Element | null> | null} */
  #preloadPromise = null;
  /** @type {Element | null} */
  #preRenderedContent = null;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // Build the selector before it is requested so opening the dialog only
    // needs to move an already-created DOM tree into place.
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => this.#preRenderQuickAdd(), { timeout: 1800 });
    } else {
      window.setTimeout(() => this.#preRenderQuickAdd(), 350);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#preRenderedContent = null;
    this.#preloadPromise = null;
    this.#preRenderQuickAdd();
  };

  /** Fetches and builds quick-add content once per product card. */
  async #loadProductGrid() {
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return null;

    const cached = this.#cachedContent.get(currentUrl);
    if (cached) return cached;
    if (this.#preloadPromise) return this.#preloadPromise;

    this.#preloadPromise = (async () => {
      const html = await this.fetchProductPage(currentUrl);
      if (!html) return null;

      const gridElement = html.querySelector('[data-product-grid-content]');
      const productGrid = gridElement
        ? /** @type {Element} */ (gridElement.cloneNode(true))
        : await this.#buildCustomProductGrid(currentUrl);

      if (productGrid) this.#cachedContent.set(currentUrl, productGrid);
      return productGrid;
    })();

    try {
      return await this.#preloadPromise;
    } finally {
      this.#preloadPromise = null;
    }
  }

  /** Creates a ready-to-display copy and warms its first image. */
  async #preRenderQuickAdd() {
    if (this.dataset.usesSellingPlans === 'true' || this.#preRenderedContent) return;

    const productGrid = await this.#loadProductGrid();
    if (!productGrid || !this.isConnected) return;

    this.#preRenderedContent = /** @type {Element} */ (productGrid.cloneNode(true));
    const image = this.#preRenderedContent.querySelector('img');
    if (image?.src) {
      const preloadImage = new Image();
      preloadImage.src = image.src;
    }
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const productGrid = await this.#loadProductGrid();

    if (productGrid) {
      const freshContent = this.#preRenderedContent
        ? /** @type {Element} */ (this.#preRenderedContent.cloneNode(true))
        : /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (productGrid.hasAttribute('data-zoter-quick-add-fallback')) {
      morph(modalContent, productGrid);
      this.#bindFallbackVariantPreview(modalContent);
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /** Restores interactive image and price updates after cloning fallback content. */
  #bindFallbackVariantPreview(root) {
    const select = root.querySelector('.zoter-quick-add-fallback__form select');
    const image = root.querySelector('.zoter-quick-add-fallback__media img');
    const price = root.querySelector('.zoter-quick-add-fallback__price');
    if (!(select instanceof HTMLSelectElement) || !(image instanceof HTMLImageElement) || !price) return;

    const update = () => {
      const selected = select.selectedOptions[0];
      if (!selected) return;
      const cents = Number(selected.dataset.price || 0);
      price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
      if (selected.dataset.image) image.src = selected.dataset.image;
    };

    select.addEventListener('change', update);
    update();
  }

  /** Builds quick-add content for custom ZOTER product templates. */
  async #buildCustomProductGrid(productPageUrl) {
    try {
      const productUrl = new URL(productPageUrl, window.location.origin);
      const response = await fetch(`${productUrl.pathname}.js`);
      if (!response.ok) return null;

      const product = await response.json();
      const variants = product.variants.filter((variant) => variant.available);
      if (!variants.length) return null;

      const getImageUrl = (value) => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        return value.src || value.url || '';
      };
      const fallbackImage = getImageUrl(product.featured_image) || getImageUrl(product.images?.[0]);

      const root = document.createElement('div');
      root.className = 'zoter-quick-add-fallback';
      root.setAttribute('data-zoter-quick-add-fallback', '');

      const media = document.createElement('div');
      media.className = 'zoter-quick-add-fallback__media';
      const image = document.createElement('img');
      image.alt = product.title;
      image.loading = 'eager';
      media.appendChild(image);

      const details = document.createElement('div');
      details.className = 'zoter-quick-add-fallback__details';
      const eyebrow = document.createElement('span');
      eyebrow.className = 'zoter-quick-add-fallback__eyebrow';
      eyebrow.textContent = 'ZOTER WORKPLACE COLLECTION';
      const title = document.createElement('h2');
      title.textContent = product.title;
      const price = document.createElement('p');
      price.className = 'zoter-quick-add-fallback__price';

      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/cart/add';
      form.className = 'zoter-quick-add-fallback__form';
      const label = document.createElement('label');
      label.htmlFor = `QuickAddVariant-${product.id}`;
      label.textContent = 'Choose model';
      const select = document.createElement('select');
      select.id = label.htmlFor;
      select.name = 'id';
      variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title;
        option.dataset.price = variant.price;
        option.dataset.image = getImageUrl(variant.featured_image) || fallbackImage;
        select.appendChild(option);
      });

      const actions = document.createElement('div');
      actions.className = 'zoter-quick-add-fallback__actions';
      const quantity = document.createElement('input');
      quantity.type = 'number';
      quantity.name = 'quantity';
      quantity.min = '1';
      quantity.value = '1';
      quantity.setAttribute('aria-label', 'Quantity');
      const submit = document.createElement('button');
      submit.type = 'submit';
      submit.textContent = 'Add to cart  →';
      actions.append(quantity, submit);

      const detailLink = document.createElement('a');
      detailLink.href = productUrl.pathname;
      detailLink.className = 'zoter-quick-add-fallback__link';
      detailLink.textContent = 'View full product details  →';
      form.append(label, select, actions);
      details.append(eyebrow, title, price, form, detailLink);
      root.append(media, details);

      const updateVariantPreview = () => {
        const selected = select.selectedOptions[0];
        const cents = Number(selected.dataset.price || 0);
        price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
        image.src = selected.dataset.image || fallbackImage;
      };
      select.addEventListener('change', updateVariantPreview);
      updateVariantPreview();

      return root;
    } catch (error) {
      console.warn('[quick-add] Unable to build custom product selector', error);
      return null;
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
